import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { Tables } from '@quoter/shared/generated/db-types'
import { supabase } from '../lib/supabase'
import { callFunction } from '../lib/functions'
import { useSession } from '../session/useSession'
import { getProduct } from '../config'
import { EVENTS, track } from '../lib/analytics'

export type SubscriptionView = Pick<
  Tables<'organization_subscriptions'>,
  | 'status'
  | 'current_period_end'
  | 'cancel_at_period_end'
  | 'trial_end'
  | 'canceled_at'
  | 'created_at'
> & { planName: string | null }

export const subscriptionKey = (orgId: string) => ['subscription', orgId] as const

/**
 * The org's latest subscription row. RLS restricts this table to admins, so
 * a member gets an empty result and the billing card simply does not render.
 * Filtered by org explicitly: RLS now admits every org the user belongs to
 * across products, so it no longer picks "the" org on its own.
 * A lapsed org (session status no_subscription) still resolves: the paywall
 * needs it too.
 */
export function useSubscription() {
  const session = useSession()
  const orgId =
    session.status === 'authenticated'
      ? session.organizationId
      : session.status === 'no_subscription'
        ? session.profile.organization_id
        : null

  const query = useQuery({
    queryKey: subscriptionKey(orgId ?? '__none'),
    enabled: !!orgId,
    staleTime: 60_000,
    queryFn: async (): Promise<SubscriptionView | null> => {
      const { data, error } = await supabase
        .from('organization_subscriptions')
        .select(
          'status, current_period_end, cancel_at_period_end, trial_end, canceled_at, created_at, pricing_plans(name)',
        )
        .eq('organization_id', orgId!)
        .order('created_at', { ascending: false })
        .limit(1)
      if (error) throw error
      const row = data?.[0]
      if (!row) return null
      const plan = row.pricing_plans as unknown as { name: string } | { name: string }[] | null
      const planName = Array.isArray(plan) ? (plan[0]?.name ?? null) : (plan?.name ?? null)
      return {
        status: row.status,
        current_period_end: row.current_period_end,
        cancel_at_period_end: row.cancel_at_period_end,
        trial_end: row.trial_end,
        canceled_at: row.canceled_at,
        created_at: row.created_at,
        planName,
      }
    },
  })

  return { subscription: query.data ?? null, loading: query.isLoading, error: query.error }
}

/**
 * Opens the Stripe Customer Portal. The URL is minted per click and never
 * stored: it is single-use and short-lived by design.
 */
export function useBillingPortal() {
  const [opening, setOpening] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function openPortal(returnTo: '/settings' | '/paywall' = '/settings') {
    if (opening) return
    setOpening(true)
    setError(null)
    const result = await callFunction<{ url: string }>('stripe-portal', { product: getProduct().id, returnTo })
    if (!result.ok || !result.data.url) {
      setError(result.ok ? 'stripe_error' : result.code)
      setOpening(false)
      return
    }
    track(EVENTS.portalOpened, { returnTo })
    window.location.href = result.data.url
  }

  return { openPortal, opening, error }
}
